// Migration Unit drill-down page. Reached via
// /sessions/:sessionId/delivery/units/:unitName.
//
// Tabs: Overview / Process map / Specs.
// Header carries back navigation, MU name, and a per-MU "Export" button
// that ships only this MU's bundle.

import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Download, FolderInput, LayoutGrid, Workflow, FileText, AlertCircle,
} from 'lucide-react';
import { useSessionFeatures } from '../hooks/useSessionFeatures';
import { useDeliveryAnalysis } from '../hooks/useDeliveryAnalysis';
import { useFeatureSpecs } from '../hooks/useFeatureSpecs';
import { combineFeatures, muColor } from '../utils/deliveryAnalysisModel';
import MigrationUnitOverview from './MigrationUnitOverview';
import MigrationUnitProcessMap from './MigrationUnitProcessMap';
import MigrationUnitSpecs from './MigrationUnitSpecs';
import SpecFolderImportModal from './SpecFolderImportModal';
import AppBrand from './AppBrand';
import '../styles/DeliveryView.css';
import '../styles/MigrationUnitView.css';

type Tab = 'overview' | 'flow' | 'specs';

const MigrationUnitView: React.FC = () => {
  const { sessionId, unitName } = useParams<{ sessionId: string; unitName: string }>();
  const decoded = unitName ? decodeURIComponent(unitName) : '';

  const sessionFeatures = useSessionFeatures(sessionId || null);
  const delivery = useDeliveryAnalysis({ sessionId: sessionId || null });
  const specs = useFeatureSpecs(sessionId || null);

  const allFeatures = useMemo(
    () => combineFeatures(
      sessionFeatures.discoveryFeatures.features,
      delivery.payload.importedFeatures,
    ),
    [sessionFeatures.discoveryFeatures.features, delivery.payload.importedFeatures],
  );
  const mu = useMemo(
    () => delivery.payload.migrationUnits.find((m) => m.name === decoded) || null,
    [delivery.payload.migrationUnits, decoded],
  );
  const muFeatures = useMemo(
    () => mu ? mu.featureIds.map((id) => allFeatures.find((f) => f.candidateId === id)).filter((f): f is NonNullable<typeof f> => !!f) : [],
    [mu, allFeatures],
  );
  const allMuNames = useMemo(
    () => delivery.payload.migrationUnits.map((m) => m.name),
    [delivery.payload.migrationUnits],
  );

  const [tab, setTab] = useState<Tab>('overview');
  const [folderImportOpen, setFolderImportOpen] = useState(false);

  const handleExport = useCallback(async () => {
    if (!sessionId || !mu) return;
    const url = `/api/sessions/${sessionId}/feature-specs/export.zip?mu=${encodeURIComponent(mu.name)}`;
    window.location.href = url;
  }, [sessionId, mu]);

  if (!sessionId) {
    return (
      <div className="dv-shell"><div className="dv-empty">Missing session id.</div></div>
    );
  }

  if (delivery.loading || sessionFeatures.loading) {
    return (
      <div className="dv-shell">
        <header className="dv-header">
          <div className="dv-header-brand">
            <Link to={`/sessions/${sessionId}/delivery`} className="dv-back-link">
              <ArrowLeft size={16} /><span>Back to Delivery view</span>
            </Link>
          </div>
        </header>
        <main className="dv-main">
          <div className="dv-empty-full"><div className="dv-empty-icon">…</div><h2>Loading…</h2></div>
        </main>
      </div>
    );
  }

  if (!mu) {
    return (
      <div className="dv-shell">
        <header className="dv-header">
          <div className="dv-header-brand">
            <Link to={`/sessions/${sessionId}/delivery`} className="dv-back-link">
              <ArrowLeft size={16} /><span>Back to Delivery view</span>
            </Link>
          </div>
        </header>
        <main className="dv-main">
          <div className="dv-empty-full">
            <div className="dv-empty-icon"><AlertCircle size={32} /></div>
            <h2>Migration Unit not found</h2>
            <p>No MU named <code>{decoded}</code> exists in this session.</p>
          </div>
        </main>
      </div>
    );
  }

  const color = muColor(mu.name);
  const specsCount = muFeatures.filter((f) => specs.payload.specs[f.candidateId]?.markdown).length;
  const hasFlow = !!mu.flowText && mu.flowText.trim().length > 0;
  const isGeneratingFlow = !!delivery.generatingFlow[mu.name];

  return (
    <div className="dv-shell">
      <header className="dv-header">
        <div className="dv-header-brand">
          <AppBrand compact />
          <span className="dv-divider" />
          <Link to={`/sessions/${sessionId}/delivery`} className="dv-back-link">
            <ArrowLeft size={16} /><span>Back to Delivery view</span>
          </Link>
          <span className="dv-divider" />
          <span className="mu-color-pip" style={{ background: color }} />
          <h1 className="mu-title">{mu.name}</h1>
          <span className="mu-feature-count">{muFeatures.length} feature{muFeatures.length === 1 ? '' : 's'}</span>
        </div>
        <div className="dv-header-actions">
          <button
            type="button"
            className="dv-btn dv-btn--ghost"
            onClick={() => setFolderImportOpen(true)}
            disabled={muFeatures.length === 0}
            title="Bulk-import specs from a folder of markdown files (matched by feature GUID)"
          >
            <FolderInput size={13} /> Import specs
          </button>
          <button
            type="button"
            className="dv-btn dv-btn--primary"
            onClick={handleExport}
            disabled={muFeatures.length === 0}
            title="Download this MU's specs as a zip"
          >
            <Download size={13} /> Export MU
          </button>
        </div>
      </header>

      <nav className="mu-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'overview'}
          className={`mu-tab ${tab === 'overview' ? 'mu-tab--active' : ''}`}
          onClick={() => setTab('overview')}
        >
          <LayoutGrid size={13} /> Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'flow'}
          className={`mu-tab ${tab === 'flow' ? 'mu-tab--active' : ''}`}
          onClick={() => setTab('flow')}
        >
          <Workflow size={13} /> Process map
          <span className={`mu-tab-pip ${hasFlow ? 'mu-tab-pip--ok' : ''}`}>{hasFlow ? '●' : '○'}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'specs'}
          className={`mu-tab ${tab === 'specs' ? 'mu-tab--active' : ''}`}
          onClick={() => setTab('specs')}
        >
          <FileText size={13} /> Specs
          <span className="mu-tab-pip">{specsCount}/{muFeatures.length}</span>
        </button>
      </nav>

      {(delivery.error || specs.error) && (
        <div className="dv-error-banner" role="alert">{delivery.error || specs.error}</div>
      )}

      <main className="dv-main mu-main">
        {tab === 'overview' && (
          <MigrationUnitOverview
            mu={mu}
            features={muFeatures}
            payload={delivery.payload}
            allMuNames={allMuNames}
            onPatch={delivery.patch}
          />
        )}
        {tab === 'flow' && (
          <MigrationUnitProcessMap
            mu={mu}
            features={muFeatures}
            hasFlow={hasFlow}
            generating={isGeneratingFlow}
            onGenerate={() => delivery.generateFlow(mu.name)}
          />
        )}
        {tab === 'specs' && (
          <MigrationUnitSpecs
            mu={mu}
            features={muFeatures}
            specsPayload={specs.payload}
            generating={specs.generating}
            saving={specs.saving}
            generatingAll={specs.generatingAll}
            onGenerate={(id, opts) => specs.generate(id, opts)}
            onSave={specs.save}
            onGenerateAll={specs.generateAll}
            onOpenFolderImport={() => setFolderImportOpen(true)}
          />
        )}
      </main>

      <SpecFolderImportModal
        isOpen={folderImportOpen}
        onClose={() => { setFolderImportOpen(false); specs.refresh(); }}
        features={muFeatures}
        specsPayload={specs.payload}
        onSave={specs.save}
        scopeLabel={mu.name}
      />
    </div>
  );
};

export default MigrationUnitView;
